import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  Select,
  TextField,
  type SelectOption
} from '@deque/cauldron-react';
import React, { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CreateApiKeyBody,
  type HttpError,
  type UpdateApiKeyBody
} from '~/common/api-client';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import styles from './AddEditApiKey.css';

/**
 * Validate the API key `name` field, returning an error message if invalid, or null if valid.
 */
export type ValidateNameCallback = (name: string) => string | null;

export interface EditParams {
  id: string;
  product_slug: string;
  name: string;
}

export interface AddEditApiKeyModalProps {
  validateName: ValidateNameCallback;
  handleSaveAdd: (params: CreateApiKeyBody) => void;
  handleSaveEdit: (params: UpdateApiKeyBody) => void;
  handleClose: () => void;
  showApiKeyModal: boolean;
  selectionOptions: SelectOption[];
  isEditMode?: boolean;
  editParams?: EditParams | null;
  error: HttpError | null;
  showLoader?: boolean;
}

const AddEditApiKey: React.FC<AddEditApiKeyModalProps> = ({
  handleSaveAdd,
  handleSaveEdit,
  handleClose,
  showApiKeyModal,
  selectionOptions,
  isEditMode = false,
  editParams = null,
  error,
  showLoader,
  validateName
}: AddEditApiKeyModalProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const selectRef = useRef<HTMLSelectElement>(null);
  const [apiKeyName, setApiKeyName] = useState(
    editParams ? editParams.name : ''
  );
  const [productName, setProductName] = useState(
    editParams ? editParams.product_slug : selectionOptions[0]?.value
  );
  const [apiKeyNameError, setApiKeyNameError] = useState('');

  const title = isEditMode ? t('EDIT API KEY') : t('ADD NEW API KEY');
  const handleChangeProduct = (e: ChangeEvent<HTMLSelectElement>) =>
    setProductName(e.target.value);
  const handleChangeApiKeyName = (value: string) => {
    setApiKeyName(value);
    setApiKeyNameError(''); // Clear error message when user types.
  };

  const onSave = (e: React.FormEvent) => {
    e.preventDefault();

    const nameError = validateName(apiKeyName);
    if (nameError) {
      setApiKeyNameError(nameError);
      return;
    }

    if (isEditMode) {
      return handleSaveEdit({ name: apiKeyName });
    }

    handleSaveAdd({
      product_slug: productName,
      name: apiKeyName,
      tags: []
    });
  };

  useEffect(() => {
    if (apiKeyNameError) {
      /* istanbul ignore next */
      inputRef.current?.focus();
    }
  }, [apiKeyNameError]);

  useEffect(() => {
    if (error?.status && error.status < 500) {
      if (error.status === 409) {
        setApiKeyNameError(t('API Key name already exists'));
      } else {
        setApiKeyNameError(error.message);
      }
    } else {
      setApiKeyNameError('');
    }
  }, [error]);

  return (
    <Modal
      heading={<span>{title}</span>}
      show={showApiKeyModal}
      onClose={handleClose}
      className={styles.apiKeyModal}
    >
      {showLoader && <ScrimmedLoader label={t('Saving...')} />}
      <form onSubmit={onSave} noValidate>
        <ModalContent>
          <Select
            disabled={isEditMode}
            label={t('Product')}
            onChange={handleChangeProduct}
            options={selectionOptions}
            value={productName}
            ref={selectRef}
          />
          <TextField
            label={t('Name')}
            error={apiKeyNameError}
            value={apiKeyName}
            onChange={handleChangeApiKeyName}
            fieldRef={inputRef}
            required
          />
        </ModalContent>
        <ModalFooter>
          <Button
            disabled={
              apiKeyName === editParams?.name ||
              // Disable save button if the product is disabled
              !!selectionOptions.find(
                s => s.value === productName && s.disabled
              )
            }
            type="submit"
          >
            {t('Save')}
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            {t('Cancel')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AddEditApiKey;
